import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const inputVariants = cva(
  'w-full bg-white border rounded-lg text-secondary placeholder:text-grey-400 focus:outline-none focus:ring-2 transition-all duration-200 disabled:bg-grey-100 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2.5 text-sm',
        lg: 'px-4 py-3',
      },
      invalid: {
        true: 'border-error focus:border-error focus:ring-error/20',
        false: 'border-grey-200 focus:border-accent focus:ring-accent/20',
      },
    },
    defaultVariants: {
      size: 'md',
      invalid: false,
    },
  },
);

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>,
    Omit<VariantProps<typeof inputVariants>, 'invalid'> {
  label?: string;
  error?: string;
  hint?: string;
  /** Leading icon (lucide). */
  icon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, size, label, error, hint, icon, id, ...props }, ref) => {
    const autoId = useId();
    const inputId = id ?? autoId;
    return (
      <div className={className}>
        {label && (
          <label htmlFor={inputId} className="label">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-grey-400 [&>svg]:h-4 [&>svg]:w-4">
              {icon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            className={cn(inputVariants({ size, invalid: Boolean(error) }), icon && 'pl-9')}
            {...props}
          />
        </div>
        {error ? (
          <p className="mt-1 text-caption text-error">{error}</p>
        ) : hint ? (
          <p className="mt-1 text-caption text-grey-500">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
